import "./MySpas.css";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RootState from "../../redux/states/root.state";
import { AppDispatch } from "../../redux/store";
import { useEffect, useRef, useState } from "react";
import { fetchUserSpas } from "../../redux/actions/spas.actions";
import Header from "../../components/common/Header/Header";
import SpaCard from "./components/SpaCard"
import EditSpaModal from "../../components/common/Modals/EditSpaModal"
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/common/Icons/Icons";
import { getPreferredUnit } from "../../utils/temperature";
import { localizeSpa } from "../../utils/brand";

const MySpas = () => {
  const dispatch: AppDispatch = useDispatch();
  const {data:spas, loading:isLoading, meta} = useSelector((state: RootState) => state.spas);
  const {data:user} = useSelector((state: RootState) => state.user);

  const [openEdit, setOpenEdit] = useState(false);
  const [spaToModify, setSpaToModify] = useState<any>(null);
  const [unit, setUnit] = useState<'F' | 'C'>(getPreferredUnit());
  // The spas slice is shared with the admin Spas list, so until this page's first fetch lands,
  // whatever is in it isn't ours: show placeholders rather than stale cards or "no spas".
  const requested = useRef(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;

  const goToPage = (value: number) => {
    setSearchParams({page: String(value)})
  }

  const handleEdit = (spa: any) => {
    setSpaToModify(spa);
    setOpenEdit(true);
  }

  useEffect(() => {
    if (searchParams.get('session_id')) {
      searchParams.delete('session_id');
      setSearchParams(searchParams);
    }
  }, [setSearchParams, searchParams]);

  useEffect(() => {
    if (user){
      requested.current = true;
      dispatch(fetchUserSpas({uuid:user?.id,page:searchParams.toString()}));
    }
  }, [dispatch, user, searchParams]);

  // Loaded once a request we made has gone loading -> done (not on the mount render, where
  // isLoading is still false from before our dispatch)
  const wasLoading = useRef(false);
  useEffect(() => {
    if (requested.current && wasLoading.current && !isLoading) setLoadedOnce(true);
    wasLoading.current = isLoading;
  }, [isLoading]);

  // Follow the °F/°C preference set in Settings
  useEffect(() => {
    const handleStorageChange = () => setUnit(getPreferredUnit());
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const total = meta?.total ?? spas.length;

  return (
    <div className="page ms-page">
      <EditSpaModal open={openEdit} setOpen={setOpenEdit} object_id={spaToModify?.id}/>

      <Header withBackButton={false} withBetaLink={true}/>

      <main className="ms-main">
        <div className="ms-head">
          <h1 className="page-heading">{localizeSpa("My Spas")}</h1>
          {loadedOnce && total > 0 &&
            <span className="ms-count">{total} {localizeSpa(total === 1 ? 'Spa' : 'Spas')}</span>}
        </div>

        {!loadedOnce || isLoading ? (
          <div className="ms-loading"><span className="spinner" role="progressbar" aria-label="Loading spas"/></div>
        ) : spas.length > 0 ? (
          <div className="ms-grid">
            {spas.map((spa) => (
              <SpaCard key={spa.id} spa={spa} unit={unit} onEdit={handleEdit}/>
            ))}
          </div>
        ) : (
          <div className="card ms-empty">
            <h2 className="ms-empty-title">{localizeSpa("No registered spas")}</h2>
            <p className="ms-empty-text">{localizeSpa("Your spas will appear here once they are registered.")}</p>
          </div>
        )}

        {/* Stays put while the next page loads */}
        {loadedOnce && totalPages > 1 && (
          <nav className="ms-pager" aria-label="Pages">
            <button type="button" className="btn btn-sm" disabled={page <= 1 || isLoading} onClick={() => goToPage(page - 1)}>
              <ChevronLeftIcon/> Previous
            </button>
            <span className="ms-pager-status">Page {page} of {totalPages}</span>
            <button type="button" className="btn btn-sm" disabled={page >= totalPages || isLoading} onClick={() => goToPage(page + 1)}>
              Next <ChevronRightIcon/>
            </button>
          </nav>
        )}
      </main>
    </div>
  );
};

export default MySpas;
